import React, { useEffect } from 'react';
// Image from next/image removed - use regular img tag;
import { useTranslation } from '../../hooks/useTranslation';
import Icon from './Icon';
import styles from './HelpModal.module.css';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <div className={styles.logoWrapper}>
              <img src="/beehive.svg" alt={t('app.name')} width={40} height={40} className={styles.logo} />
            </div>
            <div>
              <h1 className={styles.title}>{t('help.title')}</h1>
              <p className={styles.subtitle}>{t('help.subtitle')}</p>
            </div>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <Icon name="x" size={24} />
          </button>
        </div>

        <div className={styles.content}>
          {/* What is Agent Hub */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconContainer}>
                <Icon name="info" size={20} />
              </div>
              <h2>{t('help.whatIs.title')}</h2>
            </div>
            <p className={styles.sectionText}>{t('help.whatIs.description')}</p>
            
            <div className={styles.featuresGrid}>
              <div className={styles.featureCard}>
                <div className={styles.featureIcon}>
                  <Icon name="users" size={24} />
                </div>
                <h4>{t('help.whatIs.features.multiAgent')}</h4>
                <p>{t('help.whatIs.features.multiAgentDesc')}</p>
              </div>
              <div className={styles.featureCard}>
                <div className={styles.featureIcon}>
                  <Icon name="zap" size={24} />
                </div>
                <h4>{t('help.whatIs.features.realTime')}</h4>
                <p>{t('help.whatIs.features.realTimeDesc')}</p>
              </div>
              <div className={styles.featureCard}>
                <div className={styles.featureIcon}>
                  <Icon name="settings" size={24} />
                </div>
                <h4>{t('help.whatIs.features.customizable')}</h4>
                <p>{t('help.whatIs.features.customizableDesc')}</p>
              </div>
              <div className={styles.featureCard}>
                <div className={styles.featureIcon}>
                  <Icon name="trendingUp" size={24} />
                </div>
                <h4>{t('help.whatIs.features.scalable')}</h4>
                <p>{t('help.whatIs.features.scalableDesc')}</p>
              </div>
            </div>
          </div>

          {/* AI Agents */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconContainer}>
                <Icon name="users" size={20} />
              </div>
              <h2>{t('help.agents.title')}</h2>
            </div>
            <p className={styles.sectionText}>{t('help.agents.description')}</p>
            <div className={styles.agentsGrid}>
              <div className={styles.agentCard}>
                <div className={styles.agentIcon}>
                  <Icon name="edit" size={20} />
                </div>
                <div>
                  <h4>{t('help.agents.contentCreator')}</h4>
                  <p>{t('help.agents.contentCreatorDesc')}</p>
                </div>
              </div>
              <div className={styles.agentCard}>
                <div className={styles.agentIcon}>
                  <Icon name="trendingUp" size={20} />
                </div>
                <div>
                  <h4>{t('help.agents.marketing')}</h4>
                  <p>{t('help.agents.marketingDesc')}</p>
                </div>
              </div>
              <div className={styles.agentCard}>
                <div className={styles.agentIcon}>
                  <Icon name="pieChart" size={20} />
                </div>
                <div>
                  <h4>{t('help.agents.finance')}</h4>
                  <p>{t('help.agents.financeDesc')}</p>
                </div>
              </div>
              <div className={styles.agentCard}>
                <div className={styles.agentIcon}>
                  <Icon name="users" size={20} />
                </div>
                <div>
                  <h4>{t('help.agents.hr')}</h4>
                  <p>{t('help.agents.hrDesc')}</p>
                </div>
              </div>
              <div className={styles.agentCard}>
                <div className={styles.agentIcon}>
                  <Icon name="database" size={20} />
                </div>
                <div>
                  <h4>{t('help.agents.data')}</h4>
                  <p>{t('help.agents.dataDesc')}</p>
                </div>
              </div>
              <div className={styles.agentCard}>
                <div className={styles.agentIcon}>
                  <Icon name="messageCircle" size={20} />
                </div>
                <div>
                  <h4>{t('help.agents.support')}</h4>
                  <p>{t('help.agents.supportDesc')}</p>
                </div>
              </div>
              <div className={styles.agentCard}>
                <div className={styles.agentIcon}>
                  <Icon name="shield" size={20} />
                </div>
                <div>
                  <h4>{t('help.agents.legal')}</h4>
                  <p>{t('help.agents.legalDesc')}</p>
                </div>
              </div>
              <div className={styles.agentCard}>
                <div className={styles.agentIcon}>
                  <Icon name="zap" size={20} />
                </div>
                <div>
                  <h4>{t('help.agents.sales')}</h4>
                  <p>{t('help.agents.salesDesc')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Capabilities */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconContainer}>
                <Icon name="zap" size={20} />
              </div>
              <h2>{t('help.capabilitiesSection.title')}</h2>
            </div>
            <p className={styles.sectionText}>{t('help.capabilitiesSection.description')}</p>
            <div className={styles.capabilitiesGrid}>
              <div className={styles.capabilityCard}>
                <Icon name="zap" size={24} />
                <h4>{t('help.capabilitiesSection.automation.title')}</h4>
                <p>{t('help.capabilitiesSection.automation.description')}</p>
              </div>
              <div className={styles.capabilityCard}>
                <Icon name="barChart" size={24} />
                <h4>{t('help.capabilitiesSection.analytics.title')}</h4>
                <p>{t('help.capabilitiesSection.analytics.description')}</p>
              </div>
              <div className={styles.capabilityCard}>
                <Icon name="link" size={24} />
                <h4>{t('help.capabilitiesSection.integration.title')}</h4>
                <p>{t('help.capabilitiesSection.integration.description')}</p>
              </div>
              <div className={styles.capabilityCard}>
                <Icon name="users" size={24} />
                <h4>{t('help.capabilitiesSection.collaboration.title')}</h4>
                <p>{t('help.capabilitiesSection.collaboration.description')}</p>
              </div>
              <div className={styles.capabilityCard}>
                <Icon name="activity" size={24} />
                <h4>{t('help.capabilitiesSection.realTime.title')}</h4>
                <p>{t('help.capabilitiesSection.realTime.description')}</p>
              </div>
              <div className={styles.capabilityCard}>
                <Icon name="shield" size={24} />
                <h4>{t('help.capabilitiesSection.security.title')}</h4>
                <p>{t('help.capabilitiesSection.security.description')}</p>
              </div>
            </div>
          </div>

          {/* Navigation Guide */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconContainer}>
                <Icon name="navigation" size={20} />
              </div>
              <h2>{t('help.navigation.title')}</h2>
            </div>
            <p className={styles.sectionText}>{t('help.navigation.description')}</p>
            <div className={styles.navigationGrid}>
              <div className={styles.navItem}>
                <Icon name="home" size={20} />
                <div>
                  <strong>{t('help.navigation.home')}</strong>
                  <p>{t('help.navigation.homeDesc')}</p>
                </div>
              </div>
              <div className={styles.navItem}>
                <Icon name="barChart" size={20} />
                <div>
                  <strong>{t('help.navigation.dashboard')}</strong>
                  <p>{t('help.navigation.dashboardDesc')}</p>
                </div>
              </div>
              <div className={styles.navItem}>
                <Icon name="inbox" size={20} />
                <div>
                  <strong>{t('help.navigation.inbox')}</strong>
                  <p>{t('help.navigation.inboxDesc')}</p>
                </div>
              </div>
              <div className={styles.navItem}>
                <Icon name="calendar" size={20} />
                <div>
                  <strong>{t('help.navigation.agenda')}</strong>
                  <p>{t('help.navigation.agendaDesc')}</p>
                </div>
              </div>
              <div className={styles.navItem}>
                <Icon name="book" size={20} />
                <div>
                  <strong>{t('help.navigation.knowledge')}</strong>
                  <p>{t('help.navigation.knowledgeDesc')}</p>
                </div>
              </div>
              <div className={styles.navItem}>
                <Icon name="activity" size={20} />
                <div>
                  <strong>{t('help.navigation.activity')}</strong>
                  <p>{t('help.navigation.activityDesc')}</p>
                </div>
              </div>
              <div className={styles.navItem}>
                <Icon name="pieChart" size={20} />
                <div>
                  <strong>{t('help.navigation.insights')}</strong>
                  <p>{t('help.navigation.insightsDesc')}</p>
                </div>
              </div>
              <div className={styles.navItem}>
                <Icon name="tools" size={20} />
                <div>
                  <strong>{t('help.navigation.tools')}</strong>
                  <p>{t('help.navigation.toolsDesc')}</p>
                </div>
              </div>
              <div className={styles.navItem}>
                <Icon name="settings" size={20} />
                <div>
                  <strong>{t('help.navigation.settings')}</strong>
                  <p>{t('help.navigation.settingsDesc')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Getting Started */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconContainer}>
                <Icon name="play" size={20} />
              </div>
              <h2>{t('help.gettingStarted.title')}</h2>
            </div>
            <p className={styles.sectionText}>{t('help.gettingStarted.description')}</p>
            <div className={styles.stepsList}>
              <div className={styles.step}>
                <div className={styles.stepNumber}>1</div>
                <p>{t('help.gettingStarted.step1')}</p>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>2</div>
                <p>{t('help.gettingStarted.step2')}</p>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>3</div>
                <p>{t('help.gettingStarted.step3')}</p>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>4</div>
                <p>{t('help.gettingStarted.step4')}</p>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>5</div>
                <p>{t('help.gettingStarted.step5')}</p>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>6</div>
                <p>{t('help.gettingStarted.step6')}</p>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>7</div>
                <p>{t('help.gettingStarted.step7')}</p>
              </div>
              <div className={styles.step}>
                <div className={styles.stepNumber}>8</div>
                <p>{t('help.gettingStarted.step8')}</p>
              </div>
            </div>
          </div>

          {/* Use Cases */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconContainer}>
                <Icon name="target" size={20} />
              </div>
              <h2>{t('help.useCases.title')}</h2>
            </div>
            <p className={styles.sectionText}>{t('help.useCases.description')}</p>
            <div className={styles.useCasesGrid}>
              <div className={styles.useCaseCard}>
                <Icon name="edit" size={24} />
                <h4>{t('help.useCases.contentProduction.title')}</h4>
                <p>{t('help.useCases.contentProduction.description')}</p>
              </div>
              <div className={styles.useCaseCard}>
                <Icon name="barChart" size={24} />
                <h4>{t('help.useCases.dataAnalysis.title')}</h4>
                <p>{t('help.useCases.dataAnalysis.description')}</p>
              </div>
              <div className={styles.useCaseCard}>
                <Icon name="messageCircle" size={24} />
                <h4>{t('help.useCases.customerService.title')}</h4>
                <p>{t('help.useCases.customerService.description')}</p>
              </div>
              <div className={styles.useCaseCard}>
                <Icon name="users" size={24} />
                <h4>{t('help.useCases.hrAutomation.title')}</h4>
                <p>{t('help.useCases.hrAutomation.description')}</p>
              </div>
              <div className={styles.useCaseCard}>
                <Icon name="pieChart" size={24} />
                <h4>{t('help.useCases.financialReporting.title')}</h4>
                <p>{t('help.useCases.financialReporting.description')}</p>
              </div>
              <div className={styles.useCaseCard}>
                <Icon name="shield" size={24} />
                <h4>{t('help.useCases.legalCompliance.title')}</h4>
                <p>{t('help.useCases.legalCompliance.description')}</p>
              </div>
            </div>
          </div>

          {/* Pro Tips */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconContainer}>
                <Icon name="star" size={20} />
              </div>
              <h2>{t('help.tips.title')}</h2>
            </div>
            <p className={styles.sectionText}>{t('help.tips.description')}</p>
            <div className={styles.tipsList}>
              <div className={styles.tip}>
                <Icon name="checkCircle" size={16} />
                <p>{t('help.tips.tip1')}</p>
              </div>
              <div className={styles.tip}>
                <Icon name="checkCircle" size={16} />
                <p>{t('help.tips.tip2')}</p>
              </div>
              <div className={styles.tip}>
                <Icon name="checkCircle" size={16} />
                <p>{t('help.tips.tip3')}</p>
              </div>
              <div className={styles.tip}>
                <Icon name="checkCircle" size={16} />
                <p>{t('help.tips.tip4')}</p>
              </div>
              <div className={styles.tip}>
                <Icon name="checkCircle" size={16} />
                <p>{t('help.tips.tip5')}</p>
              </div>
              <div className={styles.tip}>
                <Icon name="checkCircle" size={16} />
                <p>{t('help.tips.tip6')}</p>
              </div>
              <div className={styles.tip}>
                <Icon name="checkCircle" size={16} />
                <p>{t('help.tips.tip7')}</p>
              </div>
              <div className={styles.tip}>
                <Icon name="checkCircle" size={16} />
                <p>{t('help.tips.tip8')}</p>
              </div>
            </div>
          </div>

          {/* Support & Resources */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconContainer}>
                <Icon name="helpCircle" size={20} />
              </div>
              <h2>{t('help.support.title')}</h2>
            </div>
            <p className={styles.sectionText}>{t('help.support.description')}</p>
            <div className={styles.supportGrid}>
              <div className={styles.supportItem}>
                <Icon name="messageCircle" size={24} />
                <div>
                  <strong>{t('help.support.chat')}</strong>
                  <p>{t('help.support.chatDesc')}</p>
                </div>
              </div>
              <div className={styles.supportItem}>
                <Icon name="book" size={24} />
                <div>
                  <strong>{t('help.support.docs')}</strong>
                  <p>{t('help.support.docsDesc')}</p>
                </div>
              </div>
              <div className={styles.supportItem}>
                <Icon name="users" size={24} />
                <div>
                  <strong>{t('help.support.community')}</strong>
                  <p>{t('help.support.communityDesc')}</p>
                </div>
              </div>
              <div className={styles.supportItem}>
                <Icon name="play" size={24} />
                <div>
                  <strong>{t('help.support.training')}</strong>
                  <p>{t('help.support.trainingDesc')}</p>
                </div>
              </div>
              <div className={styles.supportItem}>
                <Icon name="star" size={24} />
                <div>
                  <strong>{t('help.support.enterprise')}</strong>
                  <p>{t('help.support.enterpriseDesc')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Security & Privacy */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconContainer}>
                <Icon name="shield" size={20} />
              </div>
              <h2>{t('help.security.title')}</h2>
            </div>
            <p className={styles.sectionText}>{t('help.security.description')}</p>
            <div className={styles.securityList}>
              <div className={styles.securityItem}>
                <Icon name="lock" size={16} />
                <p>{t('help.security.encryption')}</p>
              </div>
              <div className={styles.securityItem}>
                <Icon name="checkCircle" size={16} />
                <p>{t('help.security.compliance')}</p>
              </div>
              <div className={styles.securityItem}>
                <Icon name="users" size={16} />
                <p>{t('help.security.access')}</p>
              </div>
              <div className={styles.securityItem}>
                <Icon name="database" size={16} />
                <p>{t('help.security.backup')}</p>
              </div>
              <div className={styles.securityItem}>
                <Icon name="activity" size={16} />
                <p>{t('help.security.monitoring')}</p>
              </div>
            </div>
          </div>

          {/* Roadmap */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconContainer}>
                <Icon name="mapPin" size={20} />
              </div>
              <h2>{t('help.roadmap.title')}</h2>
            </div>
            <p className={styles.sectionText}>{t('help.roadmap.description')}</p>
            <div className={styles.roadmapList}>
              <div className={styles.roadmapItem}>
                <Icon name="zap" size={16} />
                <p>{t('help.roadmap.advanced')}</p>
              </div>
              <div className={styles.roadmapItem}>
                <Icon name="smartphone" size={16} />
                <p>{t('help.roadmap.mobile')}</p>
              </div>
              <div className={styles.roadmapItem}>
                <Icon name="mic" size={16} />
                <p>{t('help.roadmap.voice')}</p>
              </div>
              <div className={styles.roadmapItem}>
                <Icon name="link" size={16} />
                <p>{t('help.roadmap.integrations')}</p>
              </div>
              <div className={styles.roadmapItem}>
                <Icon name="cpu" size={16} />
                <p>{t('help.roadmap.ai')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
